import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Process EDI 849 Response Step
 *
 * Dedicated step for processing EDI (X12) 849 response attachments from
 * shared mailbox senders (e.g., alerts-no-reply@spicetg.com).
 *
 * Unlike the Excel-based process_sd step, this step:
 * - Does NOT require upstream customer identification
 * - Identifies the supplier from the N1*SE segment in the EDI content
 * - Only processes EDI (X12) attachments, not Excel
 */
export const processEdi849Step = defineStepConfig({
  type: "process_edi_849",
  name: "Process EDI 849 Response",
  description:
    "Parse an EDI (X12) 849 response attachment and create an SD849Response record. Identifies the supplier from the EDI content.",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email containing an EDI 849 attachment",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    status: z
      .enum(["accepted", "rejected", "partial", "unknown", "skipped"])
      .describe("849 response status derived from EDI content"),
    supplierName: z
      .string()
      .nullable()
      .describe("Supplier name identified from N1*SE segment"),
    supplierId: z.string().nullable().describe("Resolved supplier database ID"),
    reason: z.string().describe("Explanation of the processing result"),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .describe("Parsed EDI data including line results and envelope info"),
    sd849ResponseId: z
      .string()
      .uuid()
      .nullable()
      .describe("ID of the created SD849Response record"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    // Matching parsed 849 lines back to 844 submission lines
    // (updateSubmissionLineStatuses) can run several minutes for large
    // batches — observed ~4.5 min for a 388-line Panduit response. The
    // previous "1 minute" timeout caused "Activity task timed out" while the
    // work was still in progress, so the timeout is raised well above the
    // observed runtime and retries use exponential backoff.
    startToCloseTimeout: "15 minutes",
    retry: {
      // 4 attempts = 1 initial + 3 retries, waiting 1m, then 2m, then 4m
      // (initialInterval 1m * backoffCoefficient 2 each time).
      maximumAttempts: 4,
      initialInterval: "1 minute",
      backoffCoefficient: 2,
      maximumInterval: "4 minutes",
    },
  },
});
